import { Layer, ThemeContext } from 'grommet';
import { StarIcon } from 'lucide-preact';
import { useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { furthestWith } from '@/components/Tours/analytics';
import { GUIDED_JOURNEY_ID } from '@/components/Tours/registry';
import { getDesktopOrMobile } from '@/helpers';
import { useSelector } from '@/redux/helper';
import { setUserData } from '@/redux/slices/user/actions';
import { buildURL, post } from '@/shared/fetch';
import { trackMP } from '@/shared/mp';

interface FeedbackModalProps {
  isSubmitted: boolean;
  onRatingSubmit: () => void;
  onFeedbackComplete: () => void;
}

const layerTheme = {
  layer: {
    background: 'transparent',
  },
};

const STAR_COLOR = '#F59E0B';

const FeedbackModal = ({ isSubmitted, onRatingSubmit, onFeedbackComplete }: FeedbackModalProps) => {
  const dispatch = useDispatch();
  // The stored high-water mark (the old guidedTour slice no longer tracks this).
  const tourState = useSelector((state) => state.tours.tours[GUIDED_JOURNEY_ID]);
  const { furthestStepIndex, furthestStepId } = furthestWith(GUIDED_JOURNEY_ID, tourState);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [loading, setLoading] = useState(false);

  const device = `${getDesktopOrMobile()}-all-access-guided-tour`;

  const handleSubmitRating = async () => {
    if (!rating || loading) return;
    trackMP('guided-tour', {
      tourName: 'All Access Guided Tour',
      action: 'rating-submitted',
      rating,
      furthestStepIndex,
      furthestStepId,
    });
    try {
      setLoading(true);
      const data = await post<{ user: any }>(buildURL('/users/rate'), { stars: rating, device });
      if (data?.user) dispatch(setUserData(data.user));
    } catch (_) {
      // Do not block tour completion if request fails.
    } finally {
      setLoading(false);
      onRatingSubmit();
    }
  };

  const handleSendFeedback = async () => {
    const text = feedbackText.trim();
    if (!text || loading) return;
    trackMP('guided-tour', {
      tourName: 'All Access Guided Tour',
      action: 'feedback-submitted',
      feedbackText: text,
      furthestStepIndex,
      furthestStepId,
    });
    try {
      setLoading(true);
      const data = await post<{ user: any }>(buildURL('/users/rate'), { feedback: text, device });
      if (data?.user) dispatch(setUserData(data.user));
    } catch (_) {
      // Do not block tour completion if request fails.
    } finally {
      setLoading(false);
      onFeedbackComplete();
    }
  };

  return (
    <ThemeContext.Extend value={layerTheme}>
      <Layer animation="fadeIn" responsive={false}>
        <div
          className={`preflight relative flex w-[740px] flex-col items-center overflow-hidden rounded-2xl bg-white pt-20 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.2)] ${
            isSubmitted ? 'pb-[41px]' : 'pb-20'
          }`}
        >
          <h2 className="preflight preflight-h2 w-full text-center text-[35px] font-bold leading-[28px] text-[#265832]">
            How Did You Like The Tour?
          </h2>

          {isSubmitted ? (
            <>
              <p className="preflight preflight-p mt-[26px] text-center text-lg text-[#265832]">
                Thanks for your feedback! Can you tell us more?
              </p>
              <textarea
                aria-label="Additional feedback"
                className="preflight mt-[26px] h-[149px] w-[631px] resize-none rounded-[4px] border border-[#cdcdcd] p-3 text-sm outline-none"
                maxLength={500}
                value={feedbackText}
                onChange={({ target }) => setFeedbackText((target as HTMLTextAreaElement).value)}
              />
              <button
                type="button"
                onClick={handleSendFeedback}
                disabled={!feedbackText.trim() || loading}
                className="preflight preflight-button mt-[26px] flex h-8 items-center justify-center rounded-full bg-[#265832] px-4 text-base font-bold text-white disabled:opacity-50"
              >
                Send
              </button>
            </>
          ) : (
            <>
              <p className="preflight preflight-p mt-[26px] text-center text-lg text-[#265832]">
                No wrong answer. This just helps us shape future guided experiences.
              </p>
              <div
                role="radiogroup"
                aria-label="Star rating"
                className="preflight mt-[26px] flex items-center gap-2"
                onMouseLeave={() => setHovered(0)}
                tabIndex={0}
              >
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    aria-label={`${star} star${star === 1 ? '' : 's'}`}
                    aria-pressed={rating === star}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHovered(star)}
                    className="preflight preflight-button cursor-pointer"
                  >
                    <StarIcon
                      className="preflight size-9"
                      color={STAR_COLOR}
                      fill={rating >= star || hovered >= star ? STAR_COLOR : 'none'}
                    />
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={handleSubmitRating}
                disabled={!rating || loading}
                className="preflight preflight-button mt-[26px] flex h-8 items-center justify-center rounded-full bg-[#265832] px-4 text-base font-bold text-white disabled:opacity-50"
              >
                Submit
              </button>
            </>
          )}
        </div>
      </Layer>
    </ThemeContext.Extend>
  );
};

export default FeedbackModal;
